/**
 * CPV My actions (/cpv/my-actions) — every CPV act waiting on the signed-in person: data sheet entry, verification and
 * approval, correction approvals, parameter master review and approval, plan review and approval, signal assessment
 * and countersign (GET /bp/cpv/my-actions/, `cpvMyActionsApi.js`).
 *
 * Everything shown is the server's: the rows and their order, the counts by kind, the due time and `overdue`, and each
 * offered act with the e-signature meaning it binds. The kind filter and the page are the server's query parameters.
 * Nothing is done from here: a row opens the object's own CPV page, where the act is taken.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Segmented, Skeleton } from 'antd';
import { Eye, Inbox, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CpvScreen, DataCard, PageHeader, StatusText, Table } from '@/components/cpv-ui';
import { StatusNotice } from '@/components/genealogy/StatusIndicator';
import { refusalOf } from './cpvApi';
import { KINDS, KIND_WORD, OBJECT_WORD, actsOf, meaningsOf, myActionsApi, openMyAction, totalOf } from './cpvMyActionsApi';
import { usePlantColumn, withPlantColumn } from './scopeParts';
import { Refusal } from './CpvPage';
import { when } from './CpvSheetParts';
import { words } from './CpvSignals';
import './CpvSheets.css';

const ALL = '';

/** The server's due time: red once it says `overdue`. */
function Due({ r, testId }) {
  if (!r.due_at && !r.overdue) return <span className="cu-muted">—</span>;
  return r.overdue ? (
    <div>
      <StatusText tone="error" label="Overdue" testId={testId} />
      {r.due_at ? <div className="cs-overdue-at">was due {when(r.due_at)}</div> : null}
    </div>
  ) : <span data-testid={testId}>{when(r.due_at)}</span>;
}

export default function CpvMyActions() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const kind = params.get('kind') || ALL;
  const page = params.get('page') || undefined;
  const plantCol = usePlantColumn();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await myActionsApi.list({ ...(kind ? { kind } : {}), ...(page ? { page } : {}) }));
    } catch (e) { setError(refusalOf(e, 'Your actions could not be read.')); }
    setLoading(false);
  }, [kind, page]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v === undefined || v === null || v === '') next.delete(k); else next.set(k, String(v)); });
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const counts = useMemo(() => data?.counts || {}, [data]);
  const kinds = KINDS.filter((k) => counts[k] !== undefined).concat(Object.keys(counts).filter((k) => !KIND_WORD[k]));
  const option = (value, label, n) => ({
    value,
    label: <span data-testid={`cma-kind-${value || 'all'}`}>{label} <b className="cu-num">{n ?? 0}</b></span>,
  });
  const rows = data?.results || [];
  const open = (r) => openMyAction(r, navigate);
  const rowKey = (r) => `${r.kind}-${r.object_type}-${r.object_id}`;

  return (
    <CpvScreen testId="cpv-my-actions">
      <PageHeader icon={Inbox} title="My actions" about="CPV acts waiting on you"
        actions={<Button icon={<RefreshCw size={16} />} onClick={load} loading={loading} aria-label="Refresh" />} />
      {error && <section className="cu-card cu-pad"><Refusal refusal={error}><Button size="small" style={{ marginTop: 8 }} onClick={load}>Retry</Button></Refusal></section>}
      {!error && !data && <section className="cu-card cu-pad"><Skeleton active /></section>}
      {data && (
        <DataCard title={kind ? KIND_WORD[kind] || kind : 'All actions'} count={data.count} subtitle="Overdue first, then the earliest due"
          tools={(
            <>
              <Segmented aria-label="Kind filter" value={kind} onChange={(v) => set({ kind: v })}
                options={[option(ALL, 'All', totalOf(counts)), ...kinds.map((k) => option(k, KIND_WORD[k] || k, counts[k]))]} />
            </>
          )}
          footer={data.pages > 1 ? (
            <div className="cs-pager">
              <span className="cu-muted cu-small">Page {data.page} of {data.pages}</span>
              <Button size="small" disabled={data.page <= 1} onClick={() => set({ page: data.page - 1 })}>Previous</Button>
              <Button size="small" disabled={data.page >= data.pages} onClick={() => set({ page: data.page + 1 })}>Next</Button>
            </div>
          ) : null}>
          {!rows.length
            ? <div className="cu-pad"><StatusNotice state="neutral" title="Nothing is waiting for your action." /></div>
            : (
              <Table testId="cma-table"
                columns={withPlantColumn(plantCol, [{ title: 'Action' }, { title: 'Object' }, { title: 'Product' }, { title: 'Offered' }, { title: 'Due' }, { title: <span className="cu-sr">Open</span> }])}>
                {rows.map((r) => {
                  const meanings = meaningsOf(r);
                  return (
                    <tr key={rowKey(r)} data-clickable onClick={() => open(r)} data-testid={`cma-row-${rowKey(r)}`}>
                      <td className="cu-cell-name">{KIND_WORD[r.kind] || r.kind}</td>
                      {plantCol.show && plantCol.cell({ plant: r.plant ?? r.plant_id })}
                      <td>
                        <button type="button" className="cu-link cu-cell-name cu-nowrap" onClick={(e) => { e.stopPropagation(); open(r); }}>{r.number}</button>
                        <div className="cu-cell-meta">{[OBJECT_WORD[r.object_type] || r.object_type, r.section ? words(r.section) : null].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td>{r.product}</td>
                      <td>
                        <div>{actsOf(r)}</div>
                        {meanings.map((m) => <div key={m} className="cu-cell-meta" data-testid="cma-meaning">Signs “{m}”</div>)}
                      </td>
                      <td className="cu-small"><Due r={r} testId={`cma-due-${rowKey(r)}`} /></td>
                      <td><Button size="small" icon={<Eye size={14} />} aria-label={`Open ${r.number}`} onClick={(e) => { e.stopPropagation(); open(r); }}>Open</Button></td>
                    </tr>
                  );
                })}
              </Table>
            )}
        </DataCard>
      )}
    </CpvScreen>
  );
}
